import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import EnterpriseMembersContext from 'providers/EnterpriseMembers/EnterpriseMembers.context';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import { isSingleElementArray } from 'utils';
import { ENTERPRISE_MEMBER_INVITATION_STATUS } from 'models/Enterprises';
import {
  BROKER_STATUS,
  BROKER_STATUS_TRANSLATION,
  SENIORITY,
} from '../components/TeamFilters/data';
import { ENTERPRISE_BUSINESS_UNIT } from '../../../../Portfolio/components/PortfolioSellerFilters/data';

export default function useTeamFilterOptions() {
  const { isOfficeManager } = useContext(CurrentUserContext);
  const { units, memberSeniorities } = useContext(EnterpriseMembersContext);

  const { t } = useTranslation();

  const statusOptions = useMemo(
    () =>
      Object.values(ENTERPRISE_MEMBER_INVITATION_STATUS).map((value) => ({
        value,
        label: t(BROKER_STATUS_TRANSLATION[value]),
      })),
    [t],
  );

  const businessUnitFilter = useMemo(
    () =>
      !isSingleElementArray(units) && !isOfficeManager
        ? {
            value: ENTERPRISE_BUSINESS_UNIT,
            label: t('businessUnit'),
            isMultiple: true,
            options: units,
          }
        : null,
    [units, isOfficeManager, t],
  );

  const statusFilter = useMemo(
    () => ({
      value: BROKER_STATUS,
      label: t('status'),
      isMultiple: true,
      options: statusOptions,
    }),
    [t, statusOptions],
  );

  const seniorityFilter = useMemo(
    () =>
      !isSingleElementArray(memberSeniorities)
        ? {
            value: SENIORITY,
            label: t('seniority'),
            isMultiple: true,
            options: memberSeniorities,
          }
        : null,
    [t, memberSeniorities],
  );

  const filterOptions = useMemo(
    () => [businessUnitFilter, statusFilter, seniorityFilter].filter(Boolean),
    [businessUnitFilter, statusFilter, seniorityFilter],
  );

  const columnFilterOptions = useMemo(
    () => ({
      ...(businessUnitFilter && {
        businessUnitName: { options: businessUnitFilter },
      }),
      invitationStatus: { options: statusFilter },
      ...(seniorityFilter && { seniority: { options: seniorityFilter } }),
    }),
    [businessUnitFilter, statusFilter, seniorityFilter],
  );

  return { filterOptions, columnFilterOptions };
}
